import { useEffect, useState } from "react";
import api, { fmtErr } from "../api";
import { useAuth } from "../AuthContext";
import { toast } from "sonner";
import { DownloadSimple, ShieldWarning } from "@phosphor-icons/react";

export const PrivacyPanel = () => {
  const { user, setUser } = useAuth();
  const [pw, setPw] = useState("");
  const [showDel, setShowDel] = useState(false);
  const [requests, setRequests] = useState([]);
  const isOp = user?.role === "operator";
  const deletionAt = user?.scheduled_deletion_at;

  const loadReqs = () => {
    if (isOp) api.get("/deletion-requests").then((r) => setRequests(r.data)).catch(() => {});
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only load by design
  useEffect(() => { loadReqs(); }, []);

  const download = async () => {
    try {
      const r = await api.get("/users/me/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `rpm-field-my-data-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Your data downloaded");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const requestDel = async () => {
    try {
      const r = await api.post("/users/me/delete-request", { password: pw });
      if (r.data.scheduled_deletion_at) {
        toast.success(`Deletion scheduled for ${r.data.scheduled_deletion_at.slice(0, 10)} — cancel any time before then`);
        const me = await api.get("/auth/me");
        setUser(me.data);
      } else {
        toast.success(`Request sent — ${r.data.contact?.name || "your service provider"} will action it`);
      }
      setShowDel(false);
      setPw("");
      loadReqs();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const cancelDel = async () => {
    try {
      await api.post("/users/me/delete-request/cancel");
      const me = await api.get("/auth/me");
      setUser(me.data);
      toast.success("Deletion cancelled — nothing is deleted");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const downloadBackup = async () => {
    try {
      const r = await api.get("/reports/backup", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `rpm-field-backup-${new Date().toISOString().slice(0, 10)}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Backup downloaded");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const resolve = async (rid) => {
    try {
      await api.post(`/deletion-requests/${rid}/resolve`);
      toast.success("Marked handled");
      loadReqs();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const pending = requests.filter((r) => r.status === "pending");

  return (
    <section className="card-flat space-y-4 p-6" data-testid="privacy-panel">
      <p className="label-caps">Privacy &amp; data</p>

      {deletionAt && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4" data-testid="deletion-window-banner">
          <p className="text-sm font-bold text-destructive">
            Account deletion scheduled for {deletionAt.slice(0, 10)}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            Your data stays available to view and export until then. Invoice and payment records are archived for 6 years (tax rules); everything else is permanently deleted.
          </p>
          {user?.deletion_source === "self" ? (
            <button data-testid="cancel-deletion-btn" onClick={cancelDel}
              className="mt-3 h-12 w-full rounded-md bg-primary text-sm font-bold text-primary-foreground">Cancel deletion — keep my account</button>
          ) : (
            <p className="mt-2 text-xs font-semibold text-muted-foreground">This window comes from your cancelled subscription — resubscribe above to cancel it automatically.</p>
          )}
        </div>
      )}

      <button data-testid="export-my-data-btn" onClick={download}
        className="flex h-12 w-full items-center justify-center gap-2 rounded-md border-2 border-primary text-sm font-bold text-primary">
        <DownloadSimple size={18} weight="bold" /> Download my data (JSON)
      </button>
      <p className="text-xs text-muted-foreground">Everything we hold about you personally — profile, and your {user?.role === "owner" ? "properties, visits, invoices and requests" : user?.role === "subcontractor" ? "crew profile, offers and leads" : "activity"}.</p>

      {isOp && (
        <>
          <button data-testid="download-backup-btn" onClick={downloadBackup}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-md border-2 border-primary text-sm font-bold text-primary">
            <DownloadSimple size={18} weight="bold" /> Download business backup (ZIP)
          </button>
          <p className="text-xs text-muted-foreground">Every business collection as JSON in one zip — your own copy, any time. API credentials are masked inside.</p>
        </>
      )}

      {!deletionAt && (
        !showDel ? (
          <button data-testid="delete-account-open" onClick={() => setShowDel(true)}
            className="h-12 w-full rounded-md border border-destructive text-sm font-bold text-destructive">Delete my account…</button>
        ) : (
          <div className="space-y-2 rounded-md border border-destructive/40 p-4" data-testid="delete-account-form">
            <p className="text-sm font-semibold text-destructive">
              {isOp ? "Delete your account and all business data" : "Request deletion of your data"}
            </p>
            <p className="text-xs text-muted-foreground">
              {isOp
                ? "You get a 90-day window to export everything, then your account and all customer data are permanently deleted (invoice/payment records are archived 6 years for tax). Enter your password to start the window."
                : "Your data is part of your service provider's business records — your request goes to them to action, and we'll tell you who to contact. Enter your password to send it."}
            </p>
            <input data-testid="delete-account-password" type="password" className="field-input w-full" placeholder="Your password"
              value={pw} onChange={(e) => setPw(e.target.value)} />
            <div className="grid grid-cols-2 gap-2">
              <button data-testid="delete-account-back" onClick={() => { setShowDel(false); setPw(""); }}
                className="h-12 rounded-md border border-border text-sm font-bold">Back</button>
              <button data-testid="delete-account-confirm" onClick={requestDel} disabled={!pw}
                className="h-12 rounded-md bg-destructive text-sm font-bold text-destructive-foreground disabled:opacity-40">
                {isOp ? "Start 90-day window" : "Send request"}
              </button>
            </div>
          </div>
        )
      )}

      {isOp && pending.length > 0 && (
        <div className="space-y-2" data-testid="privacy-requests">
          <p className="label-caps flex items-center gap-1"><ShieldWarning size={14} weight="bold" /> Privacy requests to action</p>
          {pending.map((r) => (
            <div key={r.id} className="rounded-md border border-[hsl(43,74%,50%)] bg-[hsl(43,74%,50%)]/10 p-3" data-testid={`privacy-req-${r.id}`}>
              <p className="text-sm font-semibold">{r.requester_name} <span className="text-xs font-normal text-muted-foreground">({r.requester_role}) · {r.created_at?.slice(0, 10)}</span></p>
              <p className="text-xs text-muted-foreground">{r.requester_email}</p>
              <p className="mt-1 text-xs">Asked for their personal data to be removed. Review what you can delete from your records, then reply to them directly.</p>
              <button data-testid={`privacy-resolve-${r.id}`} onClick={() => resolve(r.id)}
                className="mt-2 h-10 w-full rounded-md bg-primary text-xs font-bold text-primary-foreground">Mark handled</button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};
